import { z } from "zod";

/** Why an epoch got the campaign's default target instead of a spread-derived one. */
export const EPOCH_VOLUME_TARGET_FALLBACK_REASONS = [
  "spread_missing",
  "spread_stale",
  "spread_sample_count_too_low",
  "spread_request_failed",
  "taker_fee_not_configured",
  "no_pair_configured",
  "no_epoch_reward",
  "no_autopilot_slots",
  "no_current_target_band",
  "computed_target_too_small",
] as const;
export type EpochVolumeTargetFallbackReason = (typeof EPOCH_VOLUME_TARGET_FALLBACK_REASONS)[number];

/** The (exchange, pair) whose spread drove the computation — the widest of the campaign's combos. */
export const EpochVolumeTargetSpreadSourceSchema = z.object({
  exchangeId: z.string(),
  pairSymbol: z.string(),
  spreadPercent: z.number(),
  notionalUsd: z.number(),
  sampleCount: z.number(),
  /**
   * What this pair normally produces over the same window, from its own longer-period count.
   * Null when Kenobi had no baseline yet, so `sampleCount` was judged on the absolute floor alone.
   */
  expectedSampleCount: z.number().nullable().optional(),
  computedAt: z.string(),
  takerFeePercent: z.number(),
  /** Whether the fee came from the campaign override or the per-exchange table. */
  takerFeeSource: z.enum(["campaign_override", "exchange_table"]),
});
export type EpochVolumeTargetSpreadSource = z.infer<typeof EpochVolumeTargetSpreadSourceSchema>;

/**
 * Audit of one epoch's volume-target computation, persisted on the epoch row so the CMS can
 * show how the number was reached. `source: "default"` means a halt condition fired: `reason`
 * says which, and `appliedTargetVolume` is the campaign's `defaultEpochVolumeTarget` (or null
 * when there was none to apply, i.e. nothing was written).
 *
 * `source: "computed"` with `baselineApplied` means the spread read succeeded but priced the
 * epoch BELOW the baseline: `computedTargetVolume` is what the formula produced and
 * `appliedTargetVolume` the baseline that replaced it. The per-user fields are null whenever
 * the band was left as an admin configured it.
 */
export const EpochVolumeTargetComputationSchema = z.object({
  source: z.enum(["computed", "default"]),
  reason: z.enum(EPOCH_VOLUME_TARGET_FALLBACK_REASONS).optional(),
  computedAt: z.string(),
  spread: EpochVolumeTargetSpreadSourceSchema.optional(),
  /** Every combo considered, so an admin can see which one won and which were stale. */
  consideredSpreads: z
    .array(
      EpochVolumeTargetSpreadSourceSchema.partial().extend({
        exchangeId: z.string(),
        pairSymbol: z.string(),
        skippedReason: z.string().optional(),
      }),
    )
    .optional(),
  perEpochRewardUsd: z.number(),
  acceptableProfitUsd: z.number(),
  costPer1kUsd: z.number().optional(),
  acceptableRewardPer1kUsd: z.number().optional(),
  computedTargetVolume: z.number().optional(),
  /** The computed target was below `defaultEpochVolumeTarget`, so the baseline is what's in force. */
  baselineApplied: z.boolean().optional(),
  /** The target was already the stored config, so this run wrote nothing. */
  alreadyInPlace: z.boolean().optional(),
  /** The autopilot spread gate this epoch runs under, derived from the applied target. */
  spreadThresholdPercent: z.number().nullable().optional(),
  appliedTargetVolume: z.number().nullable(),
  appliedMaxTargetVolume: z.number().nullable(),
  appliedMinTargetVolume: z.number().nullable(),
  previousTargetVolume: z.number().nullable(),
});
export type EpochVolumeTargetComputation = z.infer<typeof EpochVolumeTargetComputationSchema>;

/** One epoch's computation as served to the CMS debug panel. */
export const EpochVolumeTargetComputationRecordSchema = z.object({
  epochId: z.number(),
  epochStart: z.coerce.date(),
  epochEnd: z.coerce.date(),
  computation: EpochVolumeTargetComputationSchema,
});
export type EpochVolumeTargetComputationRecord = z.infer<typeof EpochVolumeTargetComputationRecordSchema>;
